import React, { useState, useEffect } from "react";
import { Table, Upload } from "antd";
import {
  IconFile,
  IconPhoto,
  IconUpload,
  IconDownload,
} from "@tabler/icons-react";
import { message } from "@centrate-io/barn";
import { useCustomer } from "_contexts/useCustomer";
import { useApolloClient } from "@apollo/client";
import { portalDocuments } from "_graphql/queries/portal";
import { baseApi } from "_helpers/api";
import dayjs from "dayjs";
import "./Documents.scss";

// Mobile breakpoint in pixels (matches $breakpoint-large from variables)
const MOBILE_BREAKPOINT = 1024;

function Documents() {
  const client = useApolloClient();
  const { applicationId } = useCustomer();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
  );

  // Listen for window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchDocuments = async () => {
    if (!applicationId) return;

    try {
      const queryData = await client.query({
        variables: { application_id: applicationId },
        query: portalDocuments,
      });

      setDocuments(queryData?.data?.portalDocuments || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [applicationId]);

  const handleDownload = (document) => {
    if (document.url) {
      window.open(document.url, "_blank");
    }
  };

  const uploadProps = {
    name: "file",
    action: `${baseApi()}/application/documents`,
    multiple: true,
    showUploadList: false,
    accept: "application/pdf,image/*",
    headers: {
      Authorization: "JWT " + localStorage.getItem("JWT"),
    },
    data: {
      application_id: applicationId,
      added_by_client: true,
    },
    onChange: (info) => {
      if (info.file.status === "uploading") {
        // Optional: show loading state
      } else if (info.file.status === "done") {
        message.success(`${info.file.name} uploaded successfully`);
        fetchDocuments();
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} upload failed. Please try again.`);
      }
    },
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "N/A";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (date) => {
    return dayjs(parseInt(date)).format("MMM. D, YYYY");
  };

  const columns = [
    {
      title: "Document Name",
      key: "document",
      width: 300,
      render: (_, record) => (
        <div className="document-cell">
          <div className={`document-icon ${record.fileType || "pdf"}`}>
            {record.fileType === "pdf" || record.type === "pdf" ? (
              <IconFile size={18} />
            ) : (
              <IconPhoto size={18} />
            )}
          </div>
          <div className="document-info">
            <span className="document-name">{record.name}</span>
            <span className="document-type">
              {record.fileType?.toUpperCase() ||
                record.type?.toUpperCase() ||
                "PDF"}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Uploaded By",
      key: "uploadedBy",
      width: 130,
      render: (_, record) => (
        <span
          className={`uploaded-by-badge ${
            record.added_by_client ? "client" : "manager"
          }`}
        >
          {record.added_by_client ? "You" : "Manager"}
        </span>
      ),
    },
    {
      title: "Upload Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (date) => <span className="date-value">{formatDate(date)}</span>,
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 170,
      render: (size) => (
        <span className="size-value">{formatFileSize(size)}</span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 100,
      align: "right",
      render: (_, record) => (
        <button
          className="download-button"
          onClick={() => handleDownload(record)}
          disabled={!record.url}
        >
          <IconDownload size={16} />
        </button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="documents-page">
        <div className="dashboard-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="documents-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-row">
            <h1 className="page-title">Documents</h1>
            <div className="document-count-badge">{documents.length}</div>
          </div>
          <p className="page-subtitle">
            Access and manage all your program documents. Upload new files or
            download existing ones.
          </p>
        </div>
        <Upload {...uploadProps}>
          <button className="upload-button">
            <IconUpload size={16} />
            <span>{isMobile ? "Upload" : "Upload Document"}</span>
          </button>
        </Upload>
      </div>

      {/* Documents Table */}
      <div className="documents-card">
        {documents.length > 0 ? (
          <div className="documents-table-container">
            <Table
              columns={columns}
              dataSource={documents}
              rowKey="id"
              pagination={false}
              showHeader={true}
            />
          </div>
        ) : (
          <div className="empty-state">
            <IconFile size={48} />
            <h3>No documents yet</h3>
            <p>Upload your first document to get started</p>
            <Upload {...uploadProps}>
              <button className="upload-button">
                <IconUpload size={16} />
                <span>{isMobile ? "Upload" : "Upload Document"}</span>
              </button>
            </Upload>
          </div>
        )}
      </div>
    </div>
  );
}

export default Documents;
